import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { C, fOutfit, fBody, fMono, PublicNav, PublicFooter } from '../../Shared/Brand'

const TOOLS = [
  { title: 'Live Market Ticker', desc: 'Real-time prices across BTC, ETH, DeFi, and stablecoin pairs, refreshed every few seconds on our homepage.', to: '/resources/market-insights' },
  { title: 'Xperia Academy', desc: 'Plain-language guides covering how spot, futures, and cross-exchange arbitrage actually work.', to: '/resources/academy' },
  { title: 'Glossary', desc: 'Quick definitions for terms like spread, slippage, funding rate, and basis trade.', to: '/resources/glossary' },
  { title: 'News & Updates', desc: 'Release notes on new exchange integrations, bot improvements, and dashboard changes.', to: '/resources/news' },
]

const FAQS = [
  { q: 'What is crypto arbitrage?', a: 'Arbitrage is buying an asset on one exchange where the price is lower and simultaneously selling it on another where the price is higher, capturing the difference. Because these gaps are usually small and short-lived, it requires fast, automated execution rather than manual trading.' },
  { q: 'How does the Xperia Arbitrade Bot find opportunities?', a: 'The bot continuously monitors order books across 50+ connected exchanges, comparing bid/ask spreads in real time and automatically executing trades when a profitable gap appears, without needing you to watch the market yourself.' },
  { q: 'Is my capital at risk?', a: 'Yes. All trading, including automated arbitrage, carries risk. Market conditions, exchange downtime, and execution delays can all affect returns. Only deposit funds you can afford to have at risk, and review our Risk Disclosure before subscribing.' },
  { q: 'How often are profits paid out?', a: 'Arbitrage income is calculated and credited to your dashboard balance daily. You can track a full breakdown under Profit History on the Arbitrade Bot page once logged in.' },
  { q: 'Which exchanges does the bot connect to?', a: 'The bot currently routes across major exchanges including Binance, Bybit, KuCoin, Bitget, Bitmart, and GateIO, with more integrations added over time.' },
]

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ border: `1px solid ${C.bdr}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '10px' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', textAlign: 'left', background: C.light, border: 'none', color: C.tx, fontFamily: fBody,
        fontWeight: 600, fontSize: '14px', padding: '16px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
      }}>
        {q}
        <span style={{ color: C.primary, fontSize: '18px', flexShrink: 0, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>+</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px', fontSize: '13px', color: C.tx2, lineHeight: 1.7 }}>{a}</div>
      )}
    </div>
  )
}

function SectionTitle({ eyebrow, title, desc }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '10px' }}>{eyebrow}</div>
      <h2 style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, color: C.tx, margin: '0 0 12px' }}>{title}</h2>
      {desc && <p style={{ fontSize: '16px', fontWeight: 500, color: C.tx2, lineHeight: 1.3, maxWidth: '600px', margin: '0 auto' }}>{desc}</p>}
    </div>
  )
}

export default function ResourcesPage() {
  return (
    <div style={{ background: C.white, color: C.tx, fontFamily: fBody, minHeight: '100vh' }}>
      <style>{`
        .xr-tools-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
        @media (max-width: 640px) { .xr-hero-title { font-size: 30px !important; } }
      `}</style>
      <PublicNav active="Resources" />

      {/* HERO — dark */}
      <section style={{ padding: '140px 24px 80px', textAlign: 'center', background: C.dark, color: C.ltx, borderBottom: `1px solid ${C.dbdr}` }}>
        <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '14px' }}>Resources</div>
        <h1 className="xr-hero-title" style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(34px,5vw,52px)', lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
          Tools & Market Insights
        </h1>
        <p style={{ fontSize: '16px', fontWeight: 500, color: C.ltx2, maxWidth: '600px', margin: '0 auto', lineHeight: 1.4 }}>
          Everything you need to understand how automated arbitrage works, before and after you start trading.
        </p>
      </section>

      {/* TOOLS — light */}
      <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionTitle eyebrow="Tools" title="Explore The Resources" />
        <div className="xr-tools-grid">
          {TOOLS.map(t => (
            <Link key={t.title} to={t.to} style={{ display: 'block', textDecoration: 'none', background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontFamily: fOutfit, fontWeight: 600, fontSize: '17px', color: C.tx, marginBottom: '8px' }}>{t.title}</div>
              <p style={{ fontSize: '14px', color: C.tx2, lineHeight: 1.6, margin: 0 }}>{t.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ — light, tinted */}
      <section style={{ padding: '80px 24px', borderTop: `1px solid ${C.bdr}`, background: C.light }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <SectionTitle eyebrow="FAQ" title="Frequently Asked Questions" />
          {FAQS.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}
